import { useEffect, useRef } from 'react';
import { HiCheckCircle, HiExclamationCircle, HiInformationCircle } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { useNoticeStore } from '../store/noticeStore';

const ESTILOS = {
    success: { Icono: HiCheckCircle, color: 'text-green-500', fondo: 'bg-green-500/10' },
    error: { Icono: HiExclamationCircle, color: 'text-red-500', fondo: 'bg-red-500/10' },
    info: { Icono: HiInformationCircle, color: 'text-primary-500', fondo: 'bg-primary-500/10' }
};

/**
 * Aviso de la aplicación: modal centrado que se cierra pulsando OK.
 *
 * Se monta una sola vez en App.jsx y muestra el primer aviso de la cola. No se
 * cierra solo ni al pulsar fuera: cerrar es una decisión de la persona, para
 * que ningún mensaje se pierda mientras miras otra parte de la pantalla.
 */
export default function NoticeModal() {
    const { t } = useTranslation();
    const aviso = useNoticeStore((estado) => estado.cola[0]);
    const dismiss = useNoticeStore((estado) => estado.dismiss);
    const botonRef = useRef(null);

    useEffect(() => {
        if (!aviso) return;

        botonRef.current?.focus();

        const alPulsarTecla = (e) => {
            if (e.key === 'Escape' || e.key === 'Enter') {
                e.preventDefault();
                dismiss();
            }
        };

        window.addEventListener('keydown', alPulsarTecla);
        return () => window.removeEventListener('keydown', alPulsarTecla);
    }, [aviso, dismiss]);

    if (!aviso) return null;

    const { Icono, color, fondo } = ESTILOS[aviso.tipo] || ESTILOS.info;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"></div>

            <div
                role="alertdialog"
                aria-modal="true"
                className="relative w-full max-w-sm bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden"
            >
                <div className="p-6 flex flex-col items-center text-center gap-4">
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center ${fondo}`}>
                        <Icono className={`w-8 h-8 ${color}`} />
                    </div>
                    <p className="text-slate-900 dark:text-slate-100">{aviso.mensaje}</p>
                </div>

                <div className="px-6 pb-6 flex justify-center">
                    <button
                        ref={botonRef}
                        onClick={dismiss}
                        className="btn-primary px-10"
                    >
                        {t('common.ok')}
                    </button>
                </div>
            </div>
        </div>
    );
}
